import React, { useEffect, useState } from 'react'
import { Table, Button, Modal, Input, Space, Upload } from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SlackOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { toast } from 'react-toastify'
import { CreateProductDataAPI, GetProductDataAPI, IMG_BASE_URL, UpdateProductDataAPI } from '../../AllAPI/AdminAPI'
import axiosInstance from '../../Utils/axiosInstance'

const ProductMaster = () => {
  const selectedYear = Number(sessionStorage.getItem('year'))
  const [products, setProducts] = useState([])
  const [searchText, setSearchText] = useState('')
  const [loading, setLoading] = useState(false)

  /* ================= Modal + Inputs ================= */
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)

  const [productName, setProductName] = useState('')
  const [shortName, setShortName] = useState('')
  const [size, setSize] = useState('')
  const [photo, setPhoto] = useState(null)
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewImage, setPreviewImage] = useState('')

  useEffect(() => {
    fetchProducts()
  }, [])

  const fetchProducts = async () => {
    setLoading(true)
    try {
      const res = await axiosInstance.get(`${GetProductDataAPI}/${selectedYear}`)
      setProducts(Array.isArray(res.data) ? res.data : [])
    } catch {
      toast.error('Failed to load products')
    } finally {
      setLoading(false)
    }
  }

  /* ================= Delete ================= */
  // const [deleteId, setDeleteId] = useState(null)

  /* ================= Open Modal ================= */
  const openModal = (record = null) => {
    if (record) {
      setEditingId(record.prod_id)
      setProductName(record.product_name)
      setShortName(record.short_name)
      setSize(record.size)
      setPhoto(record.photo)
    } else {
      setEditingId(null)
      setProductName('')
      setShortName('')
      setSize('')
      setPhoto(null)
    }
    setIsModalOpen(true)
  }

  const resetForm = () => {
  setEditingId(null)
  setProductName('')
  setShortName('')
  setSize('')
  setPhoto(null)
  setPreviewImage('')
}


  /* ================= Save / Update ================= */
  const handleSave = async () => {
    if (!productName.trim()) return toast.error('Product name is required')
    if (!shortName.trim()) return toast.error('Short name is required')
    if (!size.trim()) return toast.error('Size is required')

    setLoading(true)

    try {
      const formData = new FormData()
      formData.append('product_name', productName)
      formData.append('short_name', shortName)
      formData.append('size', size)
      formData.append('year', selectedYear)

      // append photo only if selected
      if (photo) {
        formData.append('photo', photo)
      }

      if (editingId) {
        const res = await axiosInstance.put(`${UpdateProductDataAPI}/${editingId}`, formData)
        if (res.data === "UPDATED") {          
          toast.success('Product updated successfully')
        }
        else if(res.data === "ERROR"){
          toast.warn('Error Occurred! Try Again')
        }

      } else {
        const res = await axiosInstance.post(CreateProductDataAPI, formData)
         if (res.data === "SAVED") {          
          toast.success('Product added successfully')
        } else if(res.data === "ERROR"){
          toast.warn('Error Occurred! Try Again')
        }
      }

      setLoading(false)
      fetchProducts()
      setIsModalOpen(false)
      resetForm()
    } catch (error) {
      console.error(error)
      toast.error('Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  /* ================= Delete ================= */
  // const confirmDelete = () => {
  //   setProducts((prev) => prev.filter((p) => p.id !== deleteId))
  //   toast.success('Product deleted')
  //   setDeleteId(null)
  // }

  /* ================= Search ================= */
  const filteredProducts = products.filter((p) =>
    p.product_name?.toLowerCase().includes(searchText.toLowerCase()),
  )

  /* ================= Table ================= */
  const columns = [
    {
      title: 'Product Name',
      dataIndex: 'product_name',
    },
    {
      title: 'Short Name',
      dataIndex: 'short_name',
    },
    {
      title: 'Size',
      dataIndex: 'size',
    },
    {
      title: 'Photo',
      render: (_, record) =>
        record.photo ? (
          <img
            src={`${IMG_BASE_URL}${record.photo}`}
            alt="product"
            width={40}
            height={40}
            style={{
              objectFit: 'cover',
              cursor: 'pointer',
              borderRadius: 4,
            }}
            onClick={() => {
              setPreviewImage(`${IMG_BASE_URL}${record.photo}`)
              setPreviewVisible(true)
            }}
          />
        ) : (
          '—'
        ),
    },
    {
      title: 'Actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openModal(record)}
          />
          {/* <Button
            icon={<DeleteOutlined />}
            size="small"
            danger
            onClick={() => setDeleteId(record.id)}
          /> */}
        </Space>
      ),
    },
  ]

  return (
    <>
      <div className="mainContainer">
        <div className="header-row">
          <h3>
            <SlackOutlined style={{ color: 'orangered' }} /> Product Master
          </h3>
        </div>

        <div className="search-bar text-end">
          <Button
            className="addButton mx-md-2"
            icon={<PlusOutlined />}
            onClick={() => openModal()}
          >
            Add
          </Button>

          <Input
            placeholder="Search product..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredProducts}
          rowKey="id"
          loading={loading}
          bordered
          className='shadow'
          style={{ overflowX: 'auto' }}
        />
      </div>

      {/* ================= CREATE/UPDATE MODAL ================= */}
      <Modal
        title={editingId ? 'Edit Product' : 'Add Product'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={handleSave}>
            {editingId ? 'Update' : 'Save'}
          </Button>,
        ]}
      >
        <label>Product Name</label>
        <Input
          value={productName}
          onChange={(e) => setProductName(e.target.value)}
          placeholder="Enter product name"
        />
        <label className="mt-2">Short Name</label>
        <Input
          value={shortName}
          onChange={(e) => setShortName(e.target.value)}
          placeholder="Enter short name"
        />
        <label className="mt-2">Size</label>
        <Input
          value={size}
          onChange={(e) => setSize(e.target.value)}
          placeholder="Enter size"
        />
        <label className="mt-2">Upload Photo</label> <br />
        <Upload
          beforeUpload={(file) => {
            setPhoto(file)
            return false
          }}
          maxCount={1}
        >
          <Button icon={<UploadOutlined />}>Upload Photo</Button>
        </Upload>
      </Modal>

      {/* ================= IMG PREVIEW MODAL ================= */}
      <Modal
        open={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        centered
        width="auto"
        bodyStyle={{
          padding: 0,
          background: 'transparent',
          backdropFilter: 'blur(6px)',
        }}
        maskStyle={{
          backdropFilter: 'blur(6px)',
          backgroundColor: 'rgba(0,0,0,0.6)',
        }}
      >
        <div style={{ position: 'relative' }}>
          {/* Close Button */}
          {/* <Button
            type="text"
            onClick={() => setPreviewVisible(false)}
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 10,
              color: '#fff',
              fontSize: 18,
            }}
          >
            ✕
          </Button> */}

          {/* Big Image */}
          <img
            src={previewImage}
            alt="Preview"
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'block',
              borderRadius: 5,
              marginTop: '20px',
            }}
          />
        </div>
      </Modal>

      {/* ================= DELETE MODAL ================= */}
      {/* <Modal
        title="Confirm Delete"
        open={!!deleteId}
        onCancel={() => setDeleteId(null)}
        onOk={confirmDelete}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        Are you sure you want to delete this product?
      </Modal> */}
    </>
  )
}

export default ProductMaster
